import { useCallback, useEffect, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import FormElement from "rc-field-form";
import moment from "moment-timezone";
import { Card } from "reactstrap";
import WeekdaysCard from "./WeekdaysCard";
import Button from "../../../../components/Button";
import Loading from "../../../../components/Loaders/Loading";
import DateRangeFilters from "./DateRangeFilters";
import { ProfileContext } from "../../context";
import { useApiAvailability } from "../api";
import { hasDayChanges, isDayLocked, selectDayEntries, serializeDay, toFormDay, toPayloadDay } from "../helpers";
import { useCompanyTimeZone } from "../../../../utils/hooks/company";
import { useDateFilters } from "./DateRangeFilters/useDateFilters";
import { enumerateDaysBetweenDates } from "../../../TimeSheet/ManagerView/Table/useColumns";
import { useAccess } from "../../../../utils/hooks/access";
import "./style.scss";

const Content = () => {
    const { t } = useTranslation();
    const [form] = FormElement.useForm();
    const { getAvailability, create, data, loading, submitting } = useApiAvailability();
    const { from, to } = useDateFilters();
    const { isSelf, user } = useContext(ProfileContext);
    const { hasAccess: canManageUsers } = useAccess("canManageUsers");

    const timezone = useCompanyTimeZone();

    const dates = useMemo(() => {
        if (!from || !to) {
            return [];
        }
        return enumerateDaysBetweenDates(moment(from), moment(to));
    }, [from, to]);

    const url = isSelf ? "/profile/availability" : `/users/${user?.id}/availability`;

    const fetch = useCallback((controller) => {
        if ((isSelf || user?.id) && from && to) {
            getAvailability(url, { type: "temporary", startDate: from, endDate: to }, controller);
        }
    }, [getAvailability, url, isSelf, user?.id, from, to]);

    useEffect(() => {
        const controller = new AbortController();
        fetch(controller);
        return () => controller.abort();
    }, [fetch]);

    // The form edits the pending request of each day; approved records are shown read-only.
    const initialValues = useMemo(() => {
        const pending = data?.filter?.((item) => item.status === "pending");
        return dates.reduce((total, day) => ({
            ...total,
            [day]: toFormDay(selectDayEntries(pending, day).formEntry, { date: moment(day), timezone }),
        }), {});
    }, [data, dates, timezone]);

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [form, initialValues]);

    const formValues = FormElement.useWatch(undefined, form);

    const hasChanges = useMemo(
        () => (formValues ? hasDayChanges(formValues, initialValues, dates) : false),
        [formValues, initialValues, dates]
    );

    const onReset = useCallback(() => {
        form.resetFields();
        form.setFieldsValue(initialValues);
    }, [form, initialValues]);

    const onFinish = useCallback(
        (values) => {
            const days = dates.reduce((total, day) => {
                const dayData = values?.[day];

                // Only submit days whose values differ from what the form was loaded with.
                if (!dayData || serializeDay(dayData) === serializeDay(initialValues[day])) {
                    return total;
                }
                // Past days without an existing entry are read-only - never create one for them.
                if (isDayLocked(day, selectDayEntries(data, day).formEntry)) {
                    return total;
                }

                const payloadDay = toPayloadDay(day, dayData, {
                    type: "temporary",
                    date: moment(day),
                    timezone,
                });
                return payloadDay
                    ? [...total, { ...payloadDay, startDate: day, endDate: day }]
                    : total;
            }, []);

            create(days, () => fetch());
        },
        [create, fetch, data, dates, initialValues, timezone]
    );

    return (
        <>
            <div className="date-range-input-width">
                <DateRangeFilters setHasChangedDateFilter={() => {}} />
            </div>

            {loading ? (
                <div className="d-flex justify-content-center">
                    <Loading />
                </div>
            ) : (
                <FormElement form={form} onFinish={onFinish} className="h-100">
                    <Card className="days-list-height shadow-none">
                        {dates.map((day) => {
                            const { formEntry, approved, history } = selectDayEntries(data, day);
                            return (
                                <WeekdaysCard
                                    key={day}
                                    day={day}
                                    form={form}
                                    data={formEntry}
                                    approved={approved}
                                    history={history}
                                    disabled={(!isSelf && canManageUsers) || isDayLocked(day, formEntry)}
                                />
                            );
                        })}
                    </Card>
                    {isSelf && (
                        <div className="d-flex my-3 justify-content-end gap-2">
                            <Button type="button" onClick={onReset} color="muted" disabled={!hasChanges}>
                                {t("reset")}
                            </Button>
                            <Button type="submit" color="primary" disabled={!hasChanges} loading={submitting}>
                                {t("save")}
                            </Button>
                        </div>
                    )}
                </FormElement>
            )}
        </>
    );
};

export default Content;
